import { create } from 'zustand'
import api from '../api/client'

export interface SsoProvider {
  id: string
  type: string
  displayName: string
  iconUrl?: string
}

export interface PortalConfig {
  institutionName: string
  primaryColor: string
  secondaryColor: string
  topbarColor: string
  topbarTextColor: string
  sidebarColor: string
  sidebarTextColor: string
  contentBackgroundColor: string
  glassmorphism: boolean
  backgroundMode: 'solid' | 'image'
  backgroundImages: string[]
  logoUrl: string
  favicon: string
  loginBoxTitle: string
  loginBoxMessage: string
  defaultLanguage: string
  defaultDomain: string
  forceHtml5: boolean
  defaultLaunchMode: string
  searchEnabled: boolean
  layoutDefault: string
  sessionTimeout: number
  iconMode: string
  appIconSize: string
  twoFactorEnabled: boolean
  ssoProviders: SsoProvider[]
}

interface PortalState {
  config: PortalConfig | null
  loading: boolean
  error: string | null
  fetchConfig: () => Promise<void>
}

const defaultConfig: PortalConfig = {
  institutionName: 'Flexilabs',
  primaryColor: '#003399',
  secondaryColor: '#0055aa',
  topbarColor: '#003399',
  topbarTextColor: '#ffffff',
  sidebarColor: '#ffffff',
  sidebarTextColor: '#1f2937',
  contentBackgroundColor: '#f8fafc',
  glassmorphism: false,
  backgroundMode: 'solid',
  backgroundImages: [],
  logoUrl: '',
  favicon: '',
  loginBoxTitle: '',
  loginBoxMessage: '',
  defaultLanguage: 'es',
  defaultDomain: '',
  forceHtml5: false,
  defaultLaunchMode: 'native',
  searchEnabled: true,
  layoutDefault: 'grid',
  sessionTimeout: 240,
  iconMode: 'generic',
  appIconSize: 'large',
  twoFactorEnabled: false,
  ssoProviders: [],
}

export const usePortalStore = create<PortalState>((set) => ({
  config: null,
  loading: false,
  error: null,

  async fetchConfig() {
    set({ loading: true, error: null })
    try {
      const res = await api.get('/settings/portal')
      const data = res.data
      const theme = data.theme || {}
      const portal = data

      const bgMode = theme.background_mode || 'solid'

      const config: PortalConfig = {
        institutionName: data.institution_name || defaultConfig.institutionName,
        primaryColor: theme.primary_color || defaultConfig.primaryColor,
        secondaryColor: theme.secondary_color || defaultConfig.secondaryColor,
        topbarColor: theme.topbar_color || defaultConfig.topbarColor,
        topbarTextColor: theme.topbar_text_color || defaultConfig.topbarTextColor,
        sidebarColor: theme.sidebar_color || defaultConfig.sidebarColor,
        sidebarTextColor: theme.sidebar_text_color || defaultConfig.sidebarTextColor,
        contentBackgroundColor: theme.content_background_color || defaultConfig.contentBackgroundColor,
        glassmorphism: theme.glassmorphism ?? defaultConfig.glassmorphism,
        backgroundMode: bgMode === 'image' ? 'image' : 'solid',
        backgroundImages: theme.background_images || defaultConfig.backgroundImages,
        logoUrl: theme.logo_url || defaultConfig.logoUrl,
        favicon: theme.favicon || defaultConfig.favicon,
        loginBoxTitle: theme.login_box_title || defaultConfig.loginBoxTitle,
        loginBoxMessage: theme.login_box_message || defaultConfig.loginBoxMessage,
        defaultLanguage: portal.default_language || defaultConfig.defaultLanguage,
        defaultDomain: portal.default_domain || defaultConfig.defaultDomain,
        forceHtml5: portal.force_html5 ?? defaultConfig.forceHtml5,
        defaultLaunchMode: portal.default_launch_mode || defaultConfig.defaultLaunchMode,
        searchEnabled: portal.search_enabled ?? defaultConfig.searchEnabled,
        layoutDefault: portal.layout_default || defaultConfig.layoutDefault,
        sessionTimeout: portal.session_timeout || defaultConfig.sessionTimeout,
        iconMode: theme.icon_mode || defaultConfig.iconMode,
        appIconSize: theme.app_icon_size || defaultConfig.appIconSize,
        twoFactorEnabled: data.two_factor_enabled ?? defaultConfig.twoFactorEnabled,
        ssoProviders: (data.sso_providers || []).map((p: any) => ({
          id: p.id,
          type: p.type,
          displayName: p.display_name,
          iconUrl: p.icon_url,
        })),
      }

      set({ config, loading: false })

      // Update document title and favicon
      if (typeof document !== 'undefined') {
        document.title = config.institutionName
        if (config.favicon) {
          const link: HTMLLinkElement | null =
            document.querySelector("link[rel*='icon']")
          if (link) {
            link.href = config.favicon
          } else {
            const newLink = document.createElement('link')
            newLink.rel = 'icon'
            newLink.href = config.favicon
            document.head.appendChild(newLink)
          }
        }
      }
    } catch (err: any) {
      set({ error: err.message || 'Failed to load portal config', loading: false })
    }
  },
}))
